import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 5.3 visual preview at /preview/lesson/5.3
 * "The public post: writing strangers will read"
 * Vocab: Persona conditioning.
 */

export default function LessonPreviewPage5_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 5 · Taking the chain into the wild · Lesson 5.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The public post: writing strangers will read
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Persona conditioning · Hook · Honest scale</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 hook-picker drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="The thumb decides in two seconds. Most posts lose in one.">
      <p className="mt-6 text-text/90">
        A high school student wants to write a public post about teaching her
        grandmother to text. She drafts two versions. Both get posted. One gets
        seven likes from people she already knows. One gets forwarded by a
        stranger to a magazine that asks if she will write a longer piece. Same
        student. Same idea. Different first sentence.
      </p>

      <div className="mt-6 space-y-4">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Post A · Seven likes, no forwards
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;In today&apos;s rapidly evolving digital landscape, the gap
            between generations and technology is more important than ever. Many
            young people are taking on the role of teaching their grandparents
            how to use modern devices. Here are some lessons I learned along the
            way.&rdquo;
          </p>
        </div>
        <div className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            Post B · Forwarded by a stranger
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;My grandmother sent me her first text message yesterday. It
            said &lsquo;K.&rsquo; That is all. One letter and one period. It
            took her forty-five minutes. I am writing this because nothing about
            those forty-five minutes is in the user guide for the phone.&rdquo;
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        A stranger scrolling has no obligation to read your post. They will read
        the first line. If the first line is the version every other post about
        grandparents and phones already used, they keep scrolling. If the first
        line is one specific text message containing one letter and one period,
        they read sentence two.
      </p>
      <p className="mt-4 text-text/90">
        AI is excellent at writing Post A. Post A is what AI defaults to when no
        one has done the five moves first. Lesson 5.3 is about writing posts
        people who have no reason to care suddenly do.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · 5.2" title="Ask emails earn the next reply. Posts have to earn the next thumb.">
      <p className="mt-6 text-text/90">
        Lesson 5.2 was about writing to one reader who already knows who you
        are and can do something specific for you. Lesson 5.3 is about writing
        to strangers who can do nothing for you except keep reading. That
        changes which sentences are allowed to exist.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Grounding in one sentence." />
        <ReviewItem n="02" q="The three lines of an ask-email opener, in order." />
        <ReviewItem n="03" q="What turns a weak ask into a strong ask?" />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The audience is a stranger" title="The reader did not ask to read this. The first line has to make them want to.">
      <Subhead n={1} title="The vocab for this lesson" />
      <p className="mt-4 text-text/90">
        One word changes how you draft public posts. Without it, public posts
        read like an email no one asked for. With it, the post earns the reader
        line by line.
      </p>
      <VocabCard
        term="Persona conditioning"
        definition="Writing for one specific person you can picture: their device, their mood, the second of attention they have left. Persona conditioning is what you do for yourself before you draft, and what you tell AI to do when you use it. It is the opposite of writing for the average reader, who does not exist and never reads anything."
        analogy="A stand-up comic does not write a joke for &ldquo;a crowd.&rdquo; They write it for the one person who is checking their phone and might look up. If the joke lands with that person, it lands with the room. AI writing without persona conditioning is a comic writing for &ldquo;a crowd.&rdquo; Polite. Forgettable. Nobody forwards it."
        memoryHook="One person. One moment. One reason to keep reading. Otherwise you are writing for nobody."
        why="The average reader is the audience AI defaults to, and it is the audience nobody actually is. Conditioning yourself (and your AI) on a specific reader is the move that turns a generic post into something that gets stopped on, read, and shared. It is also the single most useful prompt-engineering habit you will carry into any job."
      />

      <Subhead n={2} title="The two-second hook" className="mt-12" />
      <p className="mt-4 text-text/90">
        The opening line of a public post has one job: be impossible to scroll
        past. There are four kinds of hooks that work. The first three are easy
        to use. The fourth is the one that makes a post get forwarded.
      </p>
      <HookGrid />

      <Subhead n={3} title="Honest scale" className="mt-12" />
      <p className="mt-4 text-text/90">
        Public posts get worse the bigger they try to sound. AI loves the big
        scale: &ldquo;in today&apos;s rapidly evolving landscape,&rdquo;
        &ldquo;a generation of young people,&rdquo; &ldquo;the future of
        work.&rdquo; The reader hears the size of the claim and stops trusting
        the writer. Honest scale is the antidote: tell the truth at the size of
        your actual experience.
      </p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Inflated scale
          </p>
          <p className="mt-3 text-sm text-text/85 italic">
            &ldquo;A generation of students is being shaped by AI in ways no
            previous era has ever seen.&rdquo;
          </p>
          <p className="mt-3 text-xs text-muted">
            Reader thinks: you have not met a generation.
          </p>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Honest scale
          </p>
          <p className="mt-3 text-sm text-text/85 italic">
            &ldquo;Three students in my AP Lit class used the same AI prompt
            last week. All three got the same opening sentence. None of them
            noticed.&rdquo;
          </p>
          <p className="mt-3 text-xs text-muted">
            Reader thinks: tell me what happened next.
          </p>
        </div>
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts" className="mt-12" />
      <p className="mt-4 text-text/90">
        Public posts are the most dangerous AI use case for writers because AI
        writes a recognizable shape that strangers immediately tune out. Knowing
        the split matters more here than anywhere else.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The thumb test" className="mt-12" />
      <p className="mt-4 text-text/90">
        Read your post on a phone, scrolling at normal speed. Stop the scroll
        on your own first line. Ask: would I keep reading if I did not know who
        wrote this? If the answer is &ldquo;probably not,&rdquo; rewrite the
        first sentence.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The check
        </p>
        <p className="mt-3 text-text/90">
          A good first line is a small concrete thing the reader can picture
          and a small unanswered question the reader wants resolved. Together
          they buy you sentence two.
        </p>
      </div>
    </Section>
  );
}

const HOOKS = [
  {
    n: '01',
    name: 'The detail',
    body: 'Open with one specific image the reader can see in their head. A text that said “K.” A receipt for $0.31. A coach’s hand on the door of the gym. One detail forces the reader to picture something, which is the opposite of scrolling.',
    accent: 'amber' as const,
  },
  {
    n: '02',
    name: 'The contradiction',
    body: 'Open with two things that should not be true together. “The smartest person in my Algebra class is also the worst at homework.” The reader has to keep reading to find the resolution.',
    accent: 'emerald' as const,
  },
  {
    n: '03',
    name: 'The number',
    body: 'Open with one specific number that does not sound like a generic statistic. Not “most people” or “a recent study.” A real, weird, specific number. “Forty-five minutes for two characters.”',
    accent: 'amber' as const,
  },
  {
    n: '04',
    name: 'The line you have not seen',
    body: 'The hook that gets the post forwarded. One sentence the reader has not seen on this topic before. Not contrarian for its own sake. A real, true sentence that does not appear in the average post about your subject. This is the hardest hook to write and the only one AI cannot help you with.',
    accent: 'emerald' as const,
  },
];

function HookGrid() {
  return (
    <div className="mt-6 space-y-4">
      {HOOKS.map((h) => {
        const isEm = h.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={h.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">Hook {h.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{h.name}</h4>
            </div>
            <p
              className="mt-3 text-text/90"
              dangerouslySetInnerHTML={{ __html: h.body }}
            />
          </div>
        );
      })}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Cutting word count: a 300-word post tightened to 180.',
      'Restructuring: moving the strongest line to the top.',
      'Subheads and line breaks: making the post readable on a phone.',
      'Variations: three versions of the same post to test which one lands.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'The hook: AI defaults to "in today\u2019s landscape" and other tells.',
      'The detail: AI invents details that do not match real life.',
      'Scale: AI inflates a small story into a generational claim.',
      'Voice: AI smooths out the parts that sound like a specific person.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span dangerouslySetInnerHTML={{ __html: it }} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the hook that stops the scroll" title="Three posts. Pick the opening line that earns sentence two.">
      <p className="mt-6 text-text/90">
        Each drill shows two opening lines for the same public post on the same
        platform. Pick the one a stranger would not scroll past.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <HookDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  platform: string;
  topic: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    platform: 'LinkedIn post',
    topic: 'A high school senior writing about what working a summer job taught them',
    optionA:
      '“This summer I learned what real-world work ethic looks like. Through my experience, I gained valuable skills in communication, teamwork, and problem-solving that I will carry with me into college and beyond.”',
    optionB:
      '“A customer paid for a $4.27 coffee with $4.28 and asked for the penny back. I am writing this because in three months no one has ever explained to me why I respect that customer more than the ones who tip ten dollars.”',
    correct: 'B',
    verdict:
      'Option B opens with a specific transaction, a specific amount, a specific request, and a specific contradiction the reader has not resolved. The reader is now curious about why a one-cent customer earned more respect than a ten-dollar one. That is what a hook does. Option A is every LinkedIn summer-job post ever written. The first sentence is what AI writes when the writer did not name an audience or a moment.',
  },
  {
    n: '02',
    platform: 'Op-ed in the student paper',
    topic: 'About the school’s new policy banning AI in essays',
    optionA:
      '“The new AI ban in our English department is well-intentioned and almost entirely unenforceable, and the reason is that the only people who get caught are the students who use AI badly.”',
    optionB:
      '“Artificial intelligence has become an increasingly important topic in education. Many schools are grappling with how to handle the use of AI in student work. It is important to consider the implications of these policies.”',
    correct: 'A',
    verdict:
      'Option A is a real position stated in a real voice. The contradiction (well-intentioned and unenforceable) and the consequence (only the bad users get caught) give the reader a reason to stay through the next paragraph. Option B is what AI writes when asked for “an op-ed about AI in schools.” Every sentence is true. None of them are about anything. The reader leaves before paragraph two.',
  },
  {
    n: '03',
    platform: 'Public Substack post',
    topic: 'About teaching a younger sibling to ride a bike',
    optionA:
      '“Teaching a child to ride a bike is one of the most rewarding experiences a person can have. It builds patience, fosters connection, and creates lasting memories.”',
    optionB:
      '“My sister fell forty-one times before she made it to the end of the driveway. I counted because she asked me to and because she said she wanted to know how many times not landing was part of landing.”',
    correct: 'B',
    verdict:
      'Option B opens with a number that is not a statistic, a request that is not generic, and a sentence (“how many times not landing was part of landing”) that the reader has not seen anywhere else. The hook works on the detail and the unfamiliar sentence at the same time. Option A is the inflated, generic version: AI’s default for the prompt “write about teaching a kid something.”',
  },
];

function HookDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · {drill.platform}
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.topic }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which opener stops the scroll?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block italic"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That opener gets scrolled past. See the one that does not below.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The opener that stops the scroll' : 'Correct'} · Option {drill.correct}
            </p>
            <p
              className="mt-2 text-sm text-text/90"
              dangerouslySetInnerHTML={{ __html: drill.verdict }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Persona conditioning in one sentence." answer="Writing for one specific person you can picture (their device, their mood, the second of attention they have left) before you draft, and telling AI to do the same when you use it: one person, one moment, one reason to keep reading." />
        <QuickCheckItem n="Q2" q="The four hooks that earn sentence two." answer="The detail, the contradiction, the number, and the line you have not seen before." />
        <QuickCheckItem n="Q3" q="What is honest scale, in one sentence?" answer="Telling the truth at the size of your actual experience, instead of inflating one story into a generational claim." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on a public post. Two things AI hurts." answer="Helps: cutting word count, restructuring, line breaks, generating variations. Hurts: the hook, the detail, scale, voice." />
        <QuickCheckItem n="Q5" q="What is the thumb test?" answer="Read your post on a phone at normal scrolling speed. Stop on your own first line. Ask: would I keep reading if I did not know who wrote this? If not, rewrite the first sentence." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The two-second hook map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 5.3 · Lock card · The public post
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          One person, scrolling, with one second of attention. The first line
          has to be impossible to scroll past.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Detail" body="One specific image the reader can see in their head. A text. A receipt. A score. A face." />
          <LockTile n="02" name="Contradiction" body="Two things that should not be true together. The reader has to read on to resolve them." />
          <LockTile n="03" name="Number" body="A real, weird, specific number. Not a statistic. Forty-five minutes for two characters." />
          <LockTile n="04" name="New line" body="A sentence the reader has not seen on this topic before. The forwardable hook. AI cannot write this one." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The thumb test
          </p>
          <p className="mt-2 text-text/90">
            Read on a phone at normal scrolling speed. Stop on your own first
            line. Would you keep reading if you did not know who wrote it? If
            not, rewrite.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Honest scale
          </p>
          <p className="mt-2 text-text/90">
            Tell the truth at the size of your experience. Three students in my
            class beats a generation of students. Specific small beats vague
            big.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          The reader did not ask to read this. Every sentence has to earn the
          next one.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 5.4
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The professional email: writing that becomes a record
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Outreach. Requests. Follow-ups. Escalations. Email that lives in a
            thread someone might quote back to you. Vocab: Receipt. Subject
            lines that get opened.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 5.3 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 5.2',
    'Persona',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
